"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.modifyAsset = exports.prepareStorage = exports.getAuthorization = exports.prepareModifyAsset = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const lodash_1 = require("lodash");
const internal_1 = require("../../internal");
const types_1 = require("../../types");
const utils_1 = require("../../utils");
const conversion_1 = require("../../utils/conversion");
const internal_2 = require("../../utils/internal");
/**
 * @hidden
 */
function prepareModifyAsset(args) {
    return __awaiter(this, void 0, void 0, function* () {
        const { storage, context: { polymeshApi: { tx }, }, context, } = this;
        const { asset, makeDivisible, name: newName, fundingRound: newFundingRound, identifiers: newIdentifiers, assetType: newType, } = args;
        const noArguments = makeDivisible === undefined &&
            newName === undefined &&
            newFundingRound === undefined &&
            newIdentifiers === undefined &&
            newType === undefined;
        if (noArguments) {
            throw new internal_1.PolymeshError({
                code: types_1.ErrorCode.NoDataChange,
                message: 'Nothing to modify',
            });
        }
        const rawAssetId = (0, conversion_1.assetToMeshAssetId)(asset, context);
        const [{ isDivisible, name, assetType, nonFungible }, fundingRound, identifiers] = yield Promise.all([asset.details(), asset.currentFundingRound(), asset.getIdentifiers()]);
        const transactions = [];
        if (makeDivisible) {
            if ((0, utils_1.isNftCollection)(asset)) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.UnmetPrerequisite,
                    message: 'NFT Collections cannot be made divisible',
                });
            }
            if (isDivisible) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.NoDataChange,
                    message: 'The Asset is already divisible',
                });
            }
            transactions.push((0, internal_2.checkTxType)({
                transaction: tx.asset.makeDivisible,
                args: [rawAssetId],
            }));
        }
        if (newName) {
            if (newName === name) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.NoDataChange,
                    message: 'New name is the same as current name',
                });
            }
            const nameBytes = (0, conversion_1.nameToAssetName)(newName, context);
            transactions.push((0, internal_2.checkTxType)({
                transaction: tx.asset.renameAsset,
                args: [rawAssetId, nameBytes],
            }));
        }
        if (newFundingRound) {
            if (newFundingRound === fundingRound) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.NoDataChange,
                    message: 'New funding round name is the same as current funding round',
                });
            }
            const fundingBytes = (0, conversion_1.fundingRoundToAssetFundingRound)(newFundingRound, context);
            transactions.push((0, internal_2.checkTxType)({
                transaction: tx.asset.setFundingRound,
                args: [rawAssetId, fundingBytes],
            }));
        }
        if (newIdentifiers) {
            const identifiersAreEqual = (0, internal_2.hasSameElements)(identifiers, newIdentifiers);
            if (identifiersAreEqual) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.NoDataChange,
                    message: 'New identifiers are the same as current identifiers',
                });
            }
            transactions.push((0, internal_2.checkTxType)({
                transaction: tx.asset.updateIdentifiers,
                args: [
                    rawAssetId,
                    newIdentifiers.map(newIdentifier => (0, conversion_1.securityIdentifierToAssetIdentifier)(newIdentifier, context)),
                ],
            }));
        }
        if (newType) {
            const { customTypeData } = storage;
            if ((typeof newType === 'string' && newType === assetType) ||
                (newType instanceof bignumber_js_1.default && newType.eq(assetType))) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.NoDataChange,
                    message: 'New type is the same as current type',
                });
            }
            if (nonFungible) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.ValidationError,
                    message: 'The type for a NFT Collection cannot be modified',
                });
            }
            if (typeof newType === 'string' &&
                !(0, lodash_1.values)(types_1.KnownAssetType).includes(newType) &&
                (0, lodash_1.values)(types_1.KnownNftType).includes(newType)) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.ValidationError,
                    message: 'KnownNftType cannot be used as an asset type',
                });
            }
            if (customTypeData && !customTypeData.isAlreadyCreated) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.DataUnavailable,
                    message: 'The provided custom type has not been created on the chain',
                    data: { newType },
                });
            }
            let rawType;
            if (customTypeData) {
                const { rawId } = customTypeData;
                rawType = (0, conversion_1.internalAssetTypeToAssetType)({ Custom: rawId }, context);
            }
            else {
                rawType = (0, conversion_1.internalAssetTypeToAssetType)(newType, context);
            }
            transactions.push((0, internal_2.checkTxType)({
                transaction: tx.asset.updateAssetType,
                args: [rawAssetId, rawType],
            }));
        }
        return { transactions, resolver: yield (0, internal_2.asAsset)(asset.id, context) };
    });
}
exports.prepareModifyAsset = prepareModifyAsset;
/**
 * @hidden
 */
function getAuthorization({ asset, makeDivisible, name, fundingRound, identifiers, assetType }) {
    const transactions = [];
    if (makeDivisible !== undefined) {
        transactions.push(types_1.TxTags.asset.MakeDivisible);
    }
    if (name) {
        transactions.push(types_1.TxTags.asset.RenameAsset);
    }
    if (fundingRound) {
        transactions.push(types_1.TxTags.asset.SetFundingRound);
    }
    if (identifiers) {
        transactions.push(types_1.TxTags.asset.UpdateIdentifiers);
    }
    if (assetType) {
        transactions.push(types_1.TxTags.asset.UpdateAssetType);
    }
    return {
        permissions: {
            transactions,
            portfolios: [],
            assets: [asset],
        },
    };
}
exports.getAuthorization = getAuthorization;
/**
 * @hidden
 */
function prepareStorage({ assetType }) {
    return __awaiter(this, void 0, void 0, function* () {
        const { context } = this;
        if (!assetType) {
            return { customTypeData: null };
        }
        const customTypeData = yield (0, internal_2.prepareStorageForCustomType)(assetType, (0, lodash_1.values)(types_1.KnownAssetType), context, 'modifyAsset');
        return { customTypeData };
    });
}
exports.prepareStorage = prepareStorage;
/**
 * @hidden
 */
const modifyAsset = () => new internal_1.Procedure(prepareModifyAsset, getAuthorization, prepareStorage);
exports.modifyAsset = modifyAsset;
//# sourceMappingURL=modifyAsset.js.map